/**
 * Who makes the model, as opposed to `ModelProvider` (who serves it): every
 * Azure Foundry deployment shares one provider, but GPT, Grok and Kimi are
 * different vendors. Pickers group older versions by vendor.
 */
export type ModelVendor =
  | 'Anthropic'
  | 'OpenAI'
  | 'Google'
  | 'xAI'
  | 'Mistral'
  | 'Meta'
  | 'MiniMax'
  | 'Moonshot'
  | 'Zhipu';

export type ModelProvider =
  | 'OpenAI'
  | 'Google'
  | 'Anthropic'
  | 'AzureFoundry'
  | 'LunaRoute';

/** Runtime values of a string-const record (`Object.values` widens literals in TS). */
function frozenModelValues<T extends Record<string, string>>(
  obj: T
): readonly T[keyof T][] {
  return Object.values(obj) as T[keyof T][];
}

export const LlmModels = Object.freeze({
  openAi: Object.freeze({
    Gpt4o: 'gpt-4o',
    Gpt4oMini: 'gpt-4o-mini',
    Gpt41: 'gpt-4.1',
    Gpt41Mini: 'gpt-4.1-mini',
    Gpt41Nano: 'gpt-4.1-nano',
    Gpt5: 'gpt-5',
    Gpt5Mini: 'gpt-5-mini',
    Gpt5Nano: 'gpt-5-nano',
    Gpt5Dot2: 'gpt-5.2',
    O1: 'o1',
    O3: 'o3',
    O3Mini: 'o3-mini',
    O4Mini: 'o4-mini',
  } as const),
  google: Object.freeze({
    Gemini2Dot0Flash: 'gemini-2.0-flash',
    Gemini2Dot0FlashLite: 'gemini-2.0-flash-lite',
    Gemini2Dot5ProPreview0506: 'gemini-2.5-pro-preview-05-06',
    Gemini2Dot5FlashPreview0520: 'gemini-2.5-flash-preview-05-20',
    Gemini2Dot5Flash: 'gemini-2.5-flash',
    Gemini3Pro: 'gemini-3-pro',
    Gemini3Flash: 'gemini-3-flash',
  } as const),
  anthropic: Object.freeze({
    Claude35Haiku20241022: 'claude-3-5-haiku-20241022',
    Claude37Sonnet20250219: 'claude-3-7-sonnet-20250219',
    ClaudeSonnet420250514: 'claude-sonnet-4-20250514',
    ClaudeOpus420250514: 'claude-opus-4-20250514',
    ClaudeHaiku45: 'claude-haiku-4-5',
    ClaudeSonnet45: 'claude-sonnet-4-5',
    ClaudeOpus45: 'claude-opus-4-5',
    ClaudeOpus46: 'claude-opus-4-6',
    ClaudeOpus47: 'claude-opus-4-7',
    ClaudeSonnet46: 'claude-sonnet-4-6',
    ClaudeSonnet5: 'claude-sonnet-5',
    ClaudeOpus55: 'claude-opus-5-5',
  } as const),
  azureFoundry: Object.freeze({
    AzureGpt5Dot1: 'azure-gpt-5.1',
    AzureGpt5Dot5: 'azure-gpt-5.5',
    AzureGpt5Dot5Subsidized: 'azure-gpt-5.5-subsidized',
    AzureGpt5Dot6Sol: 'azure-gpt-5.6-sol',
    CoteraUnlimited: 'cotera-unlimited',
    AzureGpt5Dot6Terra: 'azure-gpt-5.6-terra',
    AzureGpt5Dot6Luna: 'azure-gpt-5.6-luna',
    AzureGpt6Astra: 'azure-gpt-6-astra',
    AzureGpt6Sol: 'azure-gpt-6-sol',
    AzureGpt6Luna: 'azure-gpt-6-luna',
    AzureGpt5Mini: 'azure-gpt-5-mini',
    AzureGpt5Nano: 'azure-gpt-5-nano',
    Grok4Dot1Fast: 'grok-4.1-fast',
    MistralLarge3: 'mistral-large-3',
    MistralMedium3Dot5: 'mistral-medium-3.5',
    DeepseekV4Pro: 'deepseek-v4-pro',
    DeepseekV4Flash: 'deepseek-v4-flash',
    Llama4Maverick: 'llama-4-maverick',
    MinimaxM2Dot5: 'minimax-m2.5',
    Glm5Dot2: 'glm-5.2',
    FwKimiK3: 'fw-kimi-k3',
  } as const),
  lunaRoute: Object.freeze({
    Glm5Dot3Vision: 'cotera-unlimited-glm-5.3',
  } as const),
});

export type OpenAiModels =
  (typeof LlmModels.openAi)[keyof typeof LlmModels.openAi];
export type GoogleModels =
  (typeof LlmModels.google)[keyof typeof LlmModels.google];
export type AnthropicModels =
  (typeof LlmModels.anthropic)[keyof typeof LlmModels.anthropic];
export type AzureFoundryModels =
  (typeof LlmModels.azureFoundry)[keyof typeof LlmModels.azureFoundry];
export type LunaRouteModels =
  (typeof LlmModels.lunaRoute)[keyof typeof LlmModels.lunaRoute];

export type ModelId =
  | OpenAiModels
  | GoogleModels
  | AnthropicModels
  | AzureFoundryModels
  | LunaRouteModels;

export const OPEN_AI_MODELS = frozenModelValues(LlmModels.openAi);
export const GOOGLE_MODELS = frozenModelValues(LlmModels.google);
export const ANTHROPIC_MODELS = frozenModelValues(LlmModels.anthropic);
export const AZURE_FOUNDRY_MODELS = frozenModelValues(LlmModels.azureFoundry);
export const LUNAROUTE_MODELS = frozenModelValues(LlmModels.lunaRoute);

export const LLM_MODELS: readonly ModelId[] = [
  ...OPEN_AI_MODELS,
  ...GOOGLE_MODELS,
  ...ANTHROPIC_MODELS,
  ...AZURE_FOUNDRY_MODELS,
  ...LUNAROUTE_MODELS,
];

/**
 * Curated grouping used by the V1 agent model picker. A model with a `category`
 * opts into that picker; models without one are hidden from it (they may still
 * be reachable via per-org allowlists or other surfaces).
 */
export type ModelCategory = 'cheap' | 'fast' | 'deep-thinking';

export type Model = {
  id: ModelId;
  externalModelId: string;
  name: string;
  provider: ModelProvider;
  vendor: ModelVendor;
  contextWindow: number;
  maxOutputTokens: number;
  active: boolean;
  supports: {
    reasoningText: boolean;
    streaming: boolean;
    structuredOutput: boolean;
    toolUse: boolean;
    images: boolean;
    search: boolean;
    /**
     * Whether this model can return a structured (JSON-schema) response
     * AND make tool calls in the same single API call. When true, the agent
     * loop can pass `output: Output.object({ schema })` alongside `tools`,
     * removing the need for a second extractObject call to coerce JSON.
     *
     * Per-model rollout: defaults to false. Flip on after empirical validation.
     * Audit of provider docs (May 2026) is the source of truth for which
     * models are eligible — see COT-5998 research notes.
     */
    singlePassStructuredOutput: boolean;
  };
  creditMultiplier: number;
  // Azure Foundry deployments live on different Cognitive Services resources
  // depending on which region Microsoft has rolled the SKU out to. Defaults to
  // 'eastus' (the original `cotera-foundry` resource).
  azureResource?: 'eastus' | 'eastus2';
  // Which Azure OpenAI API serves the deployment. Defaults to 'chat' (Chat
  // Completions), which every Foundry model supports. 'responses' is for models
  // that refuse function tools alongside reasoning on Chat Completions (GPT-6
  // Astra, which also rejects `reasoning_effort: 'none'`).
  azureApi?: 'chat' | 'responses';
  // ---- Agent picker metadata ----
  // Curated grouping for the V1 agent model picker. When set, the model is
  // surfaced in that picker under the matching category; when absent, it is
  // hidden there. Independent of `active` — an inactive model can still be
  // categorized (e.g. the subsidized GPT-5.5), and an active model can be left
  // uncategorized to keep it out of the picker (e.g. full-price GPT-5.5).
  category?: ModelCategory;
  // One-line, user-facing blurb shown alongside the model in the picker.
  description?: string;
  // User-facing label override for pickers. Falls back to `name`. Used to hide
  // internal distinctions (e.g. the "subsidized" wording, or the "(Cotera)"
  // hosting suffix) from end users.
  displayLabel?: string;
  // ---- Superseded versions ----
  // A newer version of this model line exists. Pickers still offer it, but
  // under "Other models", grouped by vendor, below the current lineup. See
  // `isLegacyModel` and `groupLegacyModelsByVendor`.
  legacy?: true;
  // ---- Provider-outage failover ----
  // When the primary model's provider is having an outage, the ATC transparently
  // retries the call on this model instead (after exhausting a couple of
  // same-model retries — see `selectModelIdForAttempt`). This must point at
  // another *registered* model id: the ATC re-derives its resource/billing key
  // from this model's own provider + alias and re-requests resources against it,
  // so the fallback's `plan_rate_limits` rows (synced from this registry) already
  // exist. No effect when unset. Used only for the automatic hop — pickers and
  // persisted `agents.model` ids never see it.
  fallbackModelId?: ModelId;
};

type ModelMap = {
  [key in ModelId]: Model;
};

const M = LlmModels;

export const MODELS: ModelMap = {
  [M.anthropic.ClaudeSonnet46]: {
    id: M.anthropic.ClaudeSonnet46,
    legacy: true,
    externalModelId: M.anthropic.ClaudeSonnet46,
    name: 'Claude Sonnet 4.6',
    category: 'deep-thinking',
    description: 'Strong reasoning, balanced',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1048576,
    maxOutputTokens: 100000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 8,
  },
  [M.openAi.Gpt4o]: {
    id: M.openAi.Gpt4o,
    externalModelId: M.openAi.Gpt4o,
    name: 'GPT-4o',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 128000,
    maxOutputTokens: 16384,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 8,
  },
  [M.openAi.Gpt4oMini]: {
    id: M.openAi.Gpt4oMini,
    externalModelId: M.openAi.Gpt4oMini,
    name: 'GPT-4o Mini',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 128000,
    maxOutputTokens: 16384,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 1,
  },
  [M.openAi.Gpt41]: {
    id: M.openAi.Gpt41,
    externalModelId: M.openAi.Gpt41,
    name: 'GPT-4.1',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 1047576,
    maxOutputTokens: 32768,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 6,
  },
  [M.openAi.Gpt41Mini]: {
    id: M.openAi.Gpt41Mini,
    externalModelId: M.openAi.Gpt41Mini,
    name: 'GPT-4.1 Mini',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 1047576,
    maxOutputTokens: 16384,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 2,
  },
  [M.openAi.Gpt41Nano]: {
    id: M.openAi.Gpt41Nano,
    externalModelId: M.openAi.Gpt41Nano,
    name: 'GPT-4.1 Nano',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 1047576,
    maxOutputTokens: 32768,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 0.5,
  },
  [M.openAi.Gpt5]: {
    id: M.openAi.Gpt5,
    legacy: true,
    externalModelId: M.openAi.Gpt5,
    name: 'GPT-5',
    category: 'deep-thinking',
    description: 'Flagship GPT-5 with strong reasoning',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 4.5,
  },
  [M.openAi.Gpt5Mini]: {
    id: M.openAi.Gpt5Mini,
    legacy: true,
    externalModelId: M.openAi.Gpt5Mini,
    name: 'GPT-5 Mini',
    category: 'fast',
    description: 'Fast and capable',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 1,
  },
  [M.openAi.Gpt5Nano]: {
    id: M.openAi.Gpt5Nano,
    legacy: true,
    externalModelId: M.openAi.Gpt5Nano,
    name: 'GPT-5 Nano',
    category: 'cheap',
    description: 'Cheapest option for simple tasks',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 0.25,
  },
  [M.openAi.Gpt5Dot2]: {
    id: M.openAi.Gpt5Dot2,
    legacy: true,
    externalModelId: M.openAi.Gpt5Dot2,
    name: 'GPT-5.2',
    category: 'deep-thinking',
    description: 'Latest GPT-5, advanced reasoning',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 3,
  },
  [M.azureFoundry.AzureGpt5Dot1]: {
    id: M.azureFoundry.AzureGpt5Dot1,
    legacy: true,
    // Foundry-outage failover: OpenAI has no registered 5.1, so hop to the
    // nearest public flagship.
    fallbackModelId: M.openAi.Gpt5Dot2,
    externalModelId: 'gpt-5.1',
    name: 'GPT-5.1 (Cotera)',
    category: 'deep-thinking',
    description: 'Cotera-hosted GPT-5.1, large context and tool use',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 4.5,
  },
  [M.azureFoundry.AzureGpt5Dot5]: {
    id: M.azureFoundry.AzureGpt5Dot5,
    // Foundry-outage failover: OpenAI has no 5.5, nearest flagship is 5.2.
    fallbackModelId: M.openAi.Gpt5Dot2,
    externalModelId: 'gpt-5.5',
    name: 'GPT 5.5 (Cotera)',
    // This full-price model is the one surfaced in the V1 agent picker. Its
    // subsidized twin (`AzureGpt5Dot5Subsidized`) shares the same user-facing
    // label and is excluded from that picker (see `EXCLUDED_MODEL_IDS` in
    // `categorized-model-selector.tsx`), so there is no duplicate entry.
    category: 'deep-thinking',
    description: 'Cotera-hosted GPT-5.5, large context and tool use',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 6,
    // GlobalStandard for gpt-5.5 isn't available in East US yet; deployed on
    // the cotera-foundry-eastus2 resource until Microsoft backfills.
    azureResource: 'eastus2',
  },
  [M.azureFoundry.AzureGpt5Dot5Subsidized]: {
    id: M.azureFoundry.AzureGpt5Dot5Subsidized,
    // Foundry-outage failover: OpenAI has no 5.5, nearest flagship is 5.2.
    fallbackModelId: M.openAi.Gpt5Dot2,
    externalModelId: 'gpt-5.5',
    // Billed below cost against Azure credits to stay competitive during
    // early go-to-market. The "subsidized" detail must not leak to end users.
    //
    // `active: false` keeps it out of broad model pickers that filter on
    // `Model.active` (V0 chat input, V0 LLM column, Coco kimi switcher). The
    // V1 agent picker (`CategorizedModelSelector`) does not gate on `active`,
    // so it excludes this id explicitly (`EXCLUDED_MODEL_IDS`) and offers the
    // full-price `AzureGpt5Dot5` under the same user-facing label instead.
    // Agents already saved on this id still render via the picker's "Current"
    // group, using the `displayLabel` below rather than this internal `name`.
    //
    // This `name` is only ever rendered to internal users via the Coco
    // "Switch base model" picker (gated by the `coco-is-kimi` feature flag),
    // where it makes the subsidy distinction explicit. The model also backs
    // `coco.DEFAULT_MODEL`; both `getModel(id)` and the runtime model factory
    // resolve by id and ignore the `active` flag.
    name: 'GPT-5.5 (Cotera, subsidized)',
    // `displayLabel` (not `name`) is what users see in the agent picker — it
    // deliberately omits the "subsidized" wording. The full `name` is only
    // shown to internal users in the Coco "Switch base model" picker.
    displayLabel: 'GPT 5.5 (Cotera)',
    category: 'deep-thinking',
    description: 'Cotera-hosted GPT-5.5, large context and tool use',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 0.1,
    azureResource: 'eastus2',
  },
  [M.azureFoundry.AzureGpt5Dot6Sol]: {
    id: M.azureFoundry.AzureGpt5Dot6Sol,
    legacy: true,
    // Foundry-outage failover: 5.6 preview is Foundry-only, hop to public 5.2.
    fallbackModelId: M.openAi.Gpt5Dot2,
    externalModelId: 'gpt-5.6-sol',
    name: 'GPT-5.6 Sol (Cotera)',
    category: 'deep-thinking',
    description: 'Cotera-hosted GPT-5.6 flagship, large context and tool use',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    // $5/$30 per 1M tokens — same preview pricing as GPT-5.5.
    creditMultiplier: 6,
    // GPT-5.6 preview is only deployed on the eastus2 resource.
    azureResource: 'eastus2',
  },
  [M.azureFoundry.CoteraUnlimited]: {
    id: M.azureFoundry.CoteraUnlimited,
    // Foundry-outage failover: tracks AzureGpt5Dot6Sol, which it aliases.
    fallbackModelId: M.openAi.Gpt5Dot2,
    externalModelId: 'gpt-5.6-sol',
    name: 'CoteraUnlimited',
    // Hidden alias for GPT-5.6 Sol: no category keeps it out of the V1 agent
    // picker, and `active: false` keeps it out of broad active-model pickers.
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 6,
    azureResource: 'eastus2',
  },
  [M.azureFoundry.AzureGpt5Dot6Terra]: {
    id: M.azureFoundry.AzureGpt5Dot6Terra,
    legacy: true,
    // Foundry-outage failover: the mid tier maps to plain GPT-5.
    fallbackModelId: M.openAi.Gpt5,
    externalModelId: 'gpt-5.6-terra',
    name: 'GPT-5.6 Terra (Cotera)',
    category: 'fast',
    description: 'Balanced Cotera-hosted GPT-5.6 for everyday work',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    // $2.50/$15 per 1M tokens — half of GPT-5.5's preview pricing.
    creditMultiplier: 3,
    azureResource: 'eastus2',
  },
  [M.azureFoundry.AzureGpt5Dot6Luna]: {
    id: M.azureFoundry.AzureGpt5Dot6Luna,
    legacy: true,
    // Foundry-outage failover: the cheap tier maps to GPT-5 Mini.
    fallbackModelId: M.openAi.Gpt5Mini,
    externalModelId: 'gpt-5.6-luna',
    name: 'GPT-5.6 Luna (Cotera)',
    category: 'cheap',
    description: 'Fast, affordable Cotera-hosted GPT-5.6',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    // $1/$6 per 1M tokens — the cheap GPT-5.6 preview tier.
    creditMultiplier: 1.5,
    azureResource: 'eastus2',
  },
  [M.azureFoundry.AzureGpt6Astra]: {
    id: M.azureFoundry.AzureGpt6Astra,
    // Foundry-outage failover: GPT-6 is Foundry-only, hop to public 5.2.
    fallbackModelId: M.openAi.Gpt5Dot2,
    externalModelId: 'gpt-6-astra',
    name: 'GPT-6 Astra (Cotera)',
    category: 'deep-thinking',
    description: 'Cotera-hosted GPT-6 frontier model for the hardest tasks',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    // $10/$50 per 1M tokens (<272k context; $20/$75 above) — priced at the
    // Fable tier, well above Opus 5.5, hence the premium multiplier.
    creditMultiplier: 30,
    // GPT-6 is only deployed on the eastus2 resource.
    azureResource: 'eastus2',
    azureApi: 'responses',
  },
  [M.azureFoundry.AzureGpt6Sol]: {
    id: M.azureFoundry.AzureGpt6Sol,
    // Foundry-outage failover: GPT-6 is Foundry-only, hop to public 5.2.
    fallbackModelId: M.openAi.Gpt5Dot2,
    externalModelId: 'gpt-6-sol',
    name: 'GPT-6 Sol (Cotera)',
    category: 'deep-thinking',
    description: 'Cotera-hosted GPT-6, large context and tool use',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    // $2/$10 per 1M tokens (<272k context; $4/$15 above) — half of GPT-5.6
    // Sol, on par with GPT-5.6 Terra. OpenAI list price; Azure hadn't
    // published its own at launch (2026-09-23) but matches OpenAI for Astra.
    creditMultiplier: 3,
    azureResource: 'eastus2',
  },
  [M.azureFoundry.AzureGpt6Luna]: {
    id: M.azureFoundry.AzureGpt6Luna,
    // Foundry-outage failover: the cheap tier maps to GPT-5 Mini.
    fallbackModelId: M.openAi.Gpt5Mini,
    externalModelId: 'gpt-6-luna',
    name: 'GPT-6 Luna (Cotera)',
    category: 'cheap',
    description: 'Fast, affordable Cotera-hosted GPT-6',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 1050000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    // $0.10/$0.50 per 1M tokens (<272k context; $0.20/$0.75 above) — half of
    // GPT-5.6 Luna. OpenAI list price; see the Sol note above.
    creditMultiplier: 0.75,
    azureResource: 'eastus2',
  },
  [M.azureFoundry.AzureGpt5Mini]: {
    id: M.azureFoundry.AzureGpt5Mini,
    legacy: true,
    // Foundry-outage failover: same model on OpenAI directly.
    fallbackModelId: M.openAi.Gpt5Mini,
    externalModelId: M.openAi.Gpt5Mini,
    name: 'GPT-5 Mini (Cotera)',
    category: 'fast',
    description: 'Fast Cotera-hosted GPT-5 Mini',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 1,
  },
  [M.azureFoundry.AzureGpt5Nano]: {
    id: M.azureFoundry.AzureGpt5Nano,
    // Foundry-outage failover: same model on OpenAI directly.
    fallbackModelId: M.openAi.Gpt5Nano,
    externalModelId: M.openAi.Gpt5Nano,
    name: 'GPT-5 Nano (Cotera)',
    category: 'cheap',
    description: 'Cheapest Cotera-hosted option',
    provider: 'AzureFoundry',
    vendor: 'OpenAI',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 0.25,
  },
  [M.openAi.O1]: {
    id: M.openAi.O1,
    externalModelId: M.openAi.O1,
    name: 'o1',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 200000,
    maxOutputTokens: 100000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 50,
  },
  [M.openAi.O3]: {
    id: M.openAi.O3,
    legacy: true,
    externalModelId: M.openAi.O3,
    name: 'o3',
    category: 'deep-thinking',
    description: 'Advanced reasoning',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 200000,
    maxOutputTokens: 100000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 5,
  },
  [M.openAi.O3Mini]: {
    id: M.openAi.O3Mini,
    externalModelId: M.openAi.O3Mini,
    name: 'o3 Mini',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 200000,
    maxOutputTokens: 100000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: false,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 4,
  },
  [M.openAi.O4Mini]: {
    id: M.openAi.O4Mini,
    legacy: true,
    externalModelId: M.openAi.O4Mini,
    name: 'o4 Mini',
    category: 'deep-thinking',
    description: 'Advanced reasoning',
    provider: 'OpenAI',
    vendor: 'OpenAI',
    contextWindow: 200000,
    maxOutputTokens: 100000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 3,
  },
  [M.google.Gemini2Dot0Flash]: {
    id: M.google.Gemini2Dot0Flash,
    externalModelId: M.google.Gemini2Dot0Flash,
    name: 'Gemini 2.0 Flash',
    provider: 'Google',
    vendor: 'Google',
    contextWindow: 1048576,
    maxOutputTokens: 8192,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 0.5,
  },
  [M.google.Gemini2Dot0FlashLite]: {
    id: M.google.Gemini2Dot0FlashLite,
    externalModelId: M.google.Gemini2Dot0FlashLite,
    name: 'Gemini 2.0 Flash Lite',
    provider: 'Google',
    vendor: 'Google',
    contextWindow: 1048576,
    maxOutputTokens: 8192,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 0.25,
  },
  [M.google.Gemini2Dot5ProPreview0506]: {
    id: M.google.Gemini2Dot5ProPreview0506,
    externalModelId: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    provider: 'Google',
    vendor: 'Google',
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 6,
  },
  [M.google.Gemini2Dot5FlashPreview0520]: {
    id: M.google.Gemini2Dot5FlashPreview0520,
    externalModelId: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    provider: 'Google',
    vendor: 'Google',
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 2,
  },
  [M.google.Gemini2Dot5Flash]: {
    id: M.google.Gemini2Dot5Flash,
    externalModelId: M.google.Gemini2Dot5Flash,
    name: 'Gemini 2.5 Flash',
    provider: 'Google',
    vendor: 'Google',
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 2,
  },
  [M.google.Gemini3Pro]: {
    id: M.google.Gemini3Pro,
    externalModelId: 'gemini-3-pro-preview',
    name: 'Gemini 3 Pro',
    category: 'deep-thinking',
    description: 'Deep thinking, multimodal',
    provider: 'Google',
    vendor: 'Google',
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 6,
  },
  [M.google.Gemini3Flash]: {
    id: M.google.Gemini3Flash,
    externalModelId: 'gemini-3-flash-preview',
    name: 'Gemini 3 Flash',
    category: 'cheap',
    description: 'Budget-friendly, quick responses',
    provider: 'Google',
    vendor: 'Google',
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 1.5,
  },
  [M.anthropic.Claude35Haiku20241022]: {
    id: M.anthropic.Claude35Haiku20241022,
    externalModelId: M.anthropic.Claude35Haiku20241022,
    name: 'Claude 3.5 Haiku',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1048576,
    maxOutputTokens: 100000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 4,
  },
  [M.anthropic.Claude37Sonnet20250219]: {
    id: M.anthropic.Claude37Sonnet20250219,
    externalModelId: M.anthropic.Claude37Sonnet20250219,
    name: 'Claude 3.7 Sonnet',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1048576,
    maxOutputTokens: 100000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 12,
  },
  [M.anthropic.ClaudeSonnet420250514]: {
    id: M.anthropic.ClaudeSonnet420250514,
    externalModelId: M.anthropic.ClaudeSonnet420250514,
    name: 'Claude Sonnet 4',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1048576,
    maxOutputTokens: 100000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 12,
  },
  [M.anthropic.ClaudeOpus420250514]: {
    id: M.anthropic.ClaudeOpus420250514,
    externalModelId: M.anthropic.ClaudeOpus420250514,
    name: 'Claude Opus 4',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1048576,
    maxOutputTokens: 100000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 50,
  },
  [M.anthropic.ClaudeHaiku45]: {
    id: M.anthropic.ClaudeHaiku45,
    externalModelId: M.anthropic.ClaudeHaiku45,
    name: 'Claude Haiku 4.5',
    category: 'fast',
    description: 'Fast Claude with strong reasoning',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 200000,
    maxOutputTokens: 64000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 3,
  },
  // ---- Retired Claude Sonnet 4.5 ----
  // Repointed to Sonnet 4.6. The id is kept, not deleted, so persisted
  // agents/chats resolve by id; the ATC resource key stays keyed on it, so no
  // `plan_rate_limits` migration. `active: false` + no `category` removes it
  // from every picker; selections silently serve 4.6.
  [M.anthropic.ClaudeSonnet45]: {
    id: M.anthropic.ClaudeSonnet45,
    // Repointed to Sonnet 4.6 (same Sonnet tier, same multiplier).
    externalModelId: M.anthropic.ClaudeSonnet46,
    name: 'Claude Sonnet 4.5 (retired → Sonnet 4.6)',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    // Reflects the served model (Sonnet 4.6).
    contextWindow: 1048576,
    maxOutputTokens: 100000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 8,
  },
  [M.anthropic.ClaudeOpus45]: {
    id: M.anthropic.ClaudeOpus45,
    externalModelId: 'claude-opus-4-5-20251101',
    name: 'Claude Opus 4.5',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 200000,
    maxOutputTokens: 64000,
    active: false,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 20,
  },
  [M.anthropic.ClaudeOpus46]: {
    id: M.anthropic.ClaudeOpus46,
    legacy: true,
    externalModelId: M.anthropic.ClaudeOpus46,
    name: 'Claude Opus 4.6',
    category: 'deep-thinking',
    description: 'Most capable Claude, best for complex tasks',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1000000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 13,
  },
  [M.anthropic.ClaudeOpus47]: {
    id: M.anthropic.ClaudeOpus47,
    legacy: true,
    externalModelId: M.anthropic.ClaudeOpus47,
    name: 'Claude Opus 4.7',
    category: 'deep-thinking',
    description: 'Most capable Claude, best for complex tasks',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1000000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 13,
  },
  [M.azureFoundry.Grok4Dot1Fast]: {
    id: M.azureFoundry.Grok4Dot1Fast,
    externalModelId: 'grok-4-1-fast-reasoning',
    name: 'Grok 4.1 Fast',
    category: 'fast',
    description: 'Fast reasoning model hosted by Cotera',
    provider: 'AzureFoundry',
    vendor: 'xAI',
    contextWindow: 131072,
    maxOutputTokens: 16384,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 0.5,
  },
  [M.azureFoundry.MistralLarge3]: {
    id: M.azureFoundry.MistralLarge3,
    externalModelId: 'Mistral-Large-3',
    name: 'Mistral Large 3',
    category: 'fast',
    description: 'Fast multimodal model hosted by Cotera',
    provider: 'AzureFoundry',
    vendor: 'Mistral',
    contextWindow: 131072,
    maxOutputTokens: 8192,
    active: true,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: true,
    },
    creditMultiplier: 2,
  },
  [M.azureFoundry.MistralMedium3Dot5]: {
    id: M.azureFoundry.MistralMedium3Dot5,
    externalModelId: 'mistral-medium-3-5',
    name: 'Mistral Medium 3.5',
    category: 'fast',
    description: 'Dense 128B multimodal model hosted by Cotera',
    provider: 'AzureFoundry',
    vendor: 'Mistral',
    contextWindow: 262144,
    maxOutputTokens: 8192,
    active: true,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    // $1.50/$7.50 per 1M tokens at list — the dense 128B does ~3x the
    // per-token work of Large 3's 41B-active MoE, so despite the smaller
    // name it lands in the same bucket as Large 3's 2.
    creditMultiplier: 2,
  },
  // ---- Retired DeepSeek models ----
  // The DeepSeek V4 deployments have been decommissioned. The ids are kept and
  // repointed at an equivalent still-hosted model rather than deleted, because
  // agents/chats persisted with these ids resolve by id at runtime (a deleted
  // id throws "Model does not exist"), and the `plan_rate_limits` rows are keyed
  // on the resource key `llm::azure.chat::<id>::api-call`. Keeping the provider
  // on AzureFoundry (→ azure.chat) leaves that key unchanged, so no rate-limit
  // migration is needed. `active: false` + no `category` removes them from every
  // picker; existing selections silently route to the replacement below.
  [M.azureFoundry.DeepseekV4Pro]: {
    id: M.azureFoundry.DeepseekV4Pro,
    // Repointed to Kimi K3 (the closest fast open-weights model still hosted).
    externalModelId: 'FW-Kimi-K3-2',
    name: 'DeepSeek V4 Pro (retired → Kimi K3)',
    provider: 'AzureFoundry',
    vendor: 'Moonshot',
    contextWindow: 262144,
    maxOutputTokens: 32768,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: false,
      search: false,
      singlePassStructuredOutput: false,
    },
    // Billed at the served model's (Kimi K3) rate now that it backs this id.
    creditMultiplier: 3,
    azureResource: 'eastus2',
  },
  [M.azureFoundry.DeepseekV4Flash]: {
    id: M.azureFoundry.DeepseekV4Flash,
    // Repointed to MiniMax M2.5 (equivalent fast open-weights model).
    externalModelId: 'FW-MiniMax-M2.5',
    name: 'DeepSeek V4 Flash (retired → MiniMax M2.5)',
    provider: 'AzureFoundry',
    vendor: 'MiniMax',
    contextWindow: 196608,
    maxOutputTokens: 16384,
    active: false,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: false,
      search: false,
      singlePassStructuredOutput: false,
    },
    // Billed at the served model's (MiniMax M2.5) rate now that it backs this id.
    creditMultiplier: 2,
  },
  [M.azureFoundry.Llama4Maverick]: {
    id: M.azureFoundry.Llama4Maverick,
    legacy: true,
    externalModelId: 'Llama-4-Maverick-17B-128E-Instruct-FP8',
    name: 'Llama 4 Maverick',
    category: 'fast',
    description: 'Open-weights model hosted by Cotera',
    provider: 'AzureFoundry',
    vendor: 'Meta',
    contextWindow: 1048576,
    maxOutputTokens: 16384,
    active: true,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 0.5,
  },
  [M.azureFoundry.MinimaxM2Dot5]: {
    id: M.azureFoundry.MinimaxM2Dot5,
    externalModelId: 'FW-MiniMax-M2.5',
    name: 'MiniMax M2.5 (Cotera)',
    displayLabel: 'MiniMax M2.5',
    category: 'fast',
    description: 'Fast open-weights model hosted by Cotera',
    provider: 'AzureFoundry',
    vendor: 'MiniMax',
    contextWindow: 196608,
    maxOutputTokens: 16384,
    active: true,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: false,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 2,
  },
  [M.azureFoundry.Glm5Dot2]: {
    id: M.azureFoundry.Glm5Dot2,
    legacy: true,
    externalModelId: 'FW-GLM-5.2',
    name: 'GLM 5.2 (Cotera)',
    displayLabel: 'GLM 5.2',
    category: 'fast',
    description: 'Frontier-class open-weights coding model hosted by Cotera',
    provider: 'AzureFoundry',
    vendor: 'Zhipu',
    contextWindow: 1000000,
    maxOutputTokens: 131072,
    active: true,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: false,
      search: false,
      singlePassStructuredOutput: false,
    },
    // $1.40/$4.40 per 1M tokens on Fireworks serverless — between GPT-5.6
    // Luna (1.5) and Terra (3), and in line with MiniMax M2.5's 2.
    creditMultiplier: 2,
    // Fireworks-served GLM 5.2 is deployed on the cotera-foundry-eastus2
    // resource (DataZoneStandard).
    azureResource: 'eastus2',
  },
  [M.azureFoundry.FwKimiK3]: {
    id: M.azureFoundry.FwKimiK3,
    externalModelId: 'FW-Kimi-K3-2',
    name: 'Kimi K3 (Cotera)',
    displayLabel: 'Kimi K3',
    category: 'fast',
    description: 'Frontier-class open-weights agentic model hosted by Cotera',
    provider: 'AzureFoundry',
    vendor: 'Moonshot',
    contextWindow: 262144,
    maxOutputTokens: 32768,
    active: true,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: false,
      search: false,
      singlePassStructuredOutput: false,
    },
    creditMultiplier: 3,
    // Deployed on cotera-foundry-eastus2 (Data Zone Standard), same resource as
    // GLM 5.2. Points at the FW-Kimi-K3-2 deployment rather than the original
    // FW-Kimi-K3: same model and version, but provisioned at 250 req/min and
    // 250k tokens/min instead of 100/100k. The original deployment is left in
    // place so anything still pinned to it keeps working.
    azureResource: 'eastus2',
  },
  [M.anthropic.ClaudeSonnet5]: {
    id: M.anthropic.ClaudeSonnet5,
    externalModelId: M.anthropic.ClaudeSonnet5,
    name: 'Claude Sonnet 5',
    category: 'deep-thinking',
    description:
      "Anthropic's latest Sonnet - strong general-purpose agent model",
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1000000,
    maxOutputTokens: 64000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    // Standard Sonnet-tier pricing ($3/M in, $15/M out) - same as Sonnet 4.6.
    creditMultiplier: 8,
  },
  [M.anthropic.ClaudeOpus55]: {
    id: M.anthropic.ClaudeOpus55,
    externalModelId: M.anthropic.ClaudeOpus55,
    name: 'Claude Opus 5.5',
    category: 'deep-thinking',
    description: 'Most capable Claude, best for complex tasks',
    provider: 'Anthropic',
    vendor: 'Anthropic',
    contextWindow: 1000000,
    maxOutputTokens: 128000,
    active: true,
    supports: {
      reasoningText: true,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: true,
      singlePassStructuredOutput: true,
    },
    // $4/$20 per 1M tokens - cheaper than Opus 4.7, but kept at the same
    // multiplier.
    creditMultiplier: 13,
  },
  [M.lunaRoute.Glm5Dot3Vision]: {
    id: M.lunaRoute.Glm5Dot3Vision,
    externalModelId: 'glm-5.3-vision',
    // Nothing user-visible says "LunaRoute": the API returns `id` verbatim as
    // `agent.model`, so it shows on the developer-details panel, not just in
    // pickers. The id derives the ATC resource key
    // (llm::openai.chat::cotera-unlimited-glm-5.3::api-call) that
    // plan_rate_limits rows are keyed on — migration 1787598089375 moved those
    // rows, agents.model and chat_models.model together for the 5.2 rename, and
    // the 5.3 migration repeats it. Renaming the id without that migration
    // orphans the rows and hangs the allocator.
    name: 'Cotera Unlimited - GLM 5.3',
    displayLabel: 'Cotera Unlimited - GLM 5.3',
    category: 'fast',
    description: 'Unlimited-use model included with your plan',
    provider: 'LunaRoute',
    vendor: 'Zhipu',
    // From the gateway's /v1/models: context_window 524288, max_output_tokens
    // 131072 — unchanged from glm-5.2-vision, so the swap is spec-identical.
    contextWindow: 524288,
    maxOutputTokens: 131072,
    active: true,
    supports: {
      reasoningText: false,
      streaming: true,
      structuredOutput: true,
      toolUse: true,
      images: true,
      search: false,
      // Left false deliberately, and tried both ways.
      //
      // Everything measured below was measured on GLM 5.2 Vision, which this
      // entry served until the 5.3 swap. It is carried over rather than
      // re-derived: 5.3-vision reports the same capabilities from /v1/models
      // and runs through the same OpenAI-shaped path, so the failure modes are
      // assumed to hold until someone re-runs the probe. If 5.3 turns out to
      // handle single-pass structured output, this flag is the thing to flip —
      // re-measure before doing so.
      //
      // GLM 5.2 Vision is reliable while emitting tool calls and unreliable while
      // emitting free-form prose: over identical requests the tool-calling path
      // produced 110-175 tokens every run, while free-form ran to the token
      // ceiling on ~60% of runs, collapsing into an endless synonym chain. One
      // production run hit 27k output tokens against a median of 83.
      //
      // Turning this on looked like the fix — constrained decoding cannot emit a
      // synonym chain — but forcing `Output.object` into the same step as pending
      // tool calls makes streamText fail with "Tool results are missing for tool
      // calls". Real failure, not a flake.
      //
      // The mitigation instead lives in transformCallSettings, which sets
      // `structuredOutputs` for every LunaRoute call. That makes the
      // *extractObject* second pass — the one that actually runs away, and which
      // carries no tools — a genuine `response_format: json_schema` request.
      // Measured against the gateway: schema with no tools returned a valid
      // object 5/5 at 682-2196 tokens.
      //
      // Note that the free-form runaway itself has since been root-caused to the
      // `frequency_penalty: 0.5` every ATC caller hard-codes, and fixed by not
      // sending it — see transformCallSettings for the measurements. The
      // free-form path is no longer expected to run away, so `structuredOutputs`
      // and this flag are now about output *correctness*, not containment.
      singlePassStructuredOutput: false,
    },
    // Priced as a near-free inclusion rather than at cost recovery: this is the
    // "unlimited" tier, so the multiplier exists to keep usage measurable rather
    // than to bill it. Two orders of magnitude below the cheapest metered model.
    creditMultiplier: 0.01,
  },
};

export const ACTIVE_MODELS = Object.values(MODELS).filter((m) => m.active);
const ACTIVE_MODEL_IDS = ACTIVE_MODELS.map((m) => m.id);

export const getModel = (id: string): Model | undefined => {
  return MODELS[id as ModelId];
};

/** True when a newer version of the model's line has replaced it in pickers. */
export const isLegacyModel = (id: string): boolean =>
  getModel(id)?.legacy === true;

/** Order of the per-vendor "Other models" groups in pickers. */
export const MODEL_VENDORS: readonly ModelVendor[] = [
  'Anthropic',
  'OpenAI',
  'Google',
  'xAI',
  'Mistral',
  'Meta',
  'MiniMax',
  'Moonshot',
  'Zhipu',
];

/**
 * The first version number in a model's name as `[major, minor]` — "Claude
 * Opus 4.7" → [4, 7], "GPT-5 Mini" → [5, 0], "o3" → [3, 0]. Names without one
 * sort as [0, 0]. Within a vendor this tracks release order.
 */
const nameVersion = (name: string): [number, number] => {
  const match = /(\d+)(?:\.(\d+))?/.exec(name);
  return match === null
    ? [0, 0]
    : [Number(match[1]), match[2] === undefined ? 0 : Number(match[2])];
};

const byNewestVersion = (a: string, b: string): number => {
  const [aMajor, aMinor] = nameVersion(getModel(a)?.name ?? a);
  const [bMajor, bMinor] = nameVersion(getModel(b)?.name ?? b);
  return bMajor - aMajor || bMinor - aMinor;
};

/**
 * Splits picker entries into the current lineup and legacy models, the latter
 * grouped by vendor in {@link MODEL_VENDORS} order (empty groups omitted) and
 * sorted newest version first within each vendor. Current entries, and legacy
 * entries on the same version, keep their input order.
 */
export const groupLegacyModelsByVendor = <T extends { id: string }>(
  models: readonly T[]
): { current: T[]; legacyByVendor: { vendor: ModelVendor; models: T[] }[] } => {
  const current: T[] = [];
  const byVendor = new Map<ModelVendor, T[]>();
  for (const m of models) {
    const meta = getModel(m.id);
    if (meta?.legacy !== true) {
      current.push(m);
      continue;
    }
    byVendor.set(meta.vendor, [...(byVendor.get(meta.vendor) ?? []), m]);
  }
  return {
    current,
    legacyByVendor: MODEL_VENDORS.flatMap((vendor) => {
      const models = byVendor.get(vendor);
      return models === undefined
        ? []
        : [
            {
              vendor,
              models: [...models].sort((a, b) => byNewestVersion(a.id, b.id)),
            },
          ];
    }),
  };
};

// ---- Agent picker categories ----
// The V1 agent model picker groups models by `Model.category`. Everything it
// needs (label, description, ordering) is derived from the `MODELS` registry so
// the registry is the single, exhaustive source of truth — add a `category` to
// a model and it shows up automatically.

/** A model surfaced in the curated agent picker, with display metadata. */
export type CategorizedModel = {
  id: ModelId;
  category: ModelCategory;
  label: string;
  description: string;
};

const CATEGORY_LABELS: Record<ModelCategory, string> = {
  cheap: 'Cheap',
  fast: 'Fast',
  'deep-thinking': 'Deep thinking',
};

/** Order in which categories are rendered in the picker. */
export const MODEL_CATEGORIES: readonly ModelCategory[] = [
  'cheap',
  'fast',
  'deep-thinking',
];

export const getCategoryLabel = (category: ModelCategory): string =>
  CATEGORY_LABELS[category];

/**
 * Every model that opts into the curated agent picker (i.e. has a `category`),
 * in registry order. `label` prefers `displayLabel` over the internal `name`.
 */
export const CATEGORIZED_MODELS: readonly CategorizedModel[] = Object.values(
  MODELS
)
  .filter(
    (m): m is Model & { category: ModelCategory } => m.category !== undefined
  )
  .map((m) => ({
    id: m.id,
    category: m.category,
    label: m.displayLabel ?? m.name,
    description: m.description ?? '',
  }));

export const getDefaultModelForCategory = (
  category: ModelCategory
): ModelId | undefined =>
  CATEGORIZED_MODELS.find((m) => m.category === category)?.id;

// One-off per-org model allowlist used to restrict which models a single org can
// see/select in the UI pickers. Orgs absent from this map see ALL models. Keyed
// on the immutable org UUID — slugs regenerate on rename, so they're unsafe to
// hardcode. Restricted to the OpenAI-on-Azure models plus Mistral Large 3.
const AZURE_ONLY_MODELS: readonly ModelId[] = [
  LlmModels.azureFoundry.AzureGpt5Dot5Subsidized,
  LlmModels.azureFoundry.AzureGpt5Dot5,
  LlmModels.azureFoundry.AzureGpt5Dot6Sol,
  LlmModels.azureFoundry.AzureGpt5Dot6Terra,
  LlmModels.azureFoundry.AzureGpt5Dot6Luna,
  LlmModels.azureFoundry.AzureGpt6Astra,
  LlmModels.azureFoundry.AzureGpt6Sol,
  LlmModels.azureFoundry.AzureGpt6Luna,
  LlmModels.azureFoundry.AzureGpt5Dot1,
  LlmModels.azureFoundry.AzureGpt5Mini,
  LlmModels.azureFoundry.AzureGpt5Nano,
  LlmModels.azureFoundry.MistralLarge3,
];

export const ORG_MODEL_ALLOWLIST: Record<string, readonly ModelId[]> = {
  'a7ff73a5-e291-4cf6-85de-7c35b5195f23': AZURE_ONLY_MODELS,
};

/** The hardcoded model allowlist for an org, or undefined if unrestricted. */
export const orgModelAllowlist = (
  orgId: string | undefined
): readonly ModelId[] | undefined => {
  if (orgId === undefined) return undefined;
  return ORG_MODEL_ALLOWLIST[orgId];
};

/** Filters a model list to those allowed for the org (default = unchanged). */
export const allowedModelsForOrg = <T extends { id: ModelId }>(
  orgId: string | undefined,
  models: readonly T[]
): readonly T[] => {
  const allow = orgModelAllowlist(orgId);
  if (allow === undefined) return models;
  const set = new Set<ModelId>(allow);
  return models.filter((m) => set.has(m.id));
};
